import React, { useState, useEffect } from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import { useTranslation } from "react-i18next";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import { styled } from "@mui/material/styles";
import { useDispatch, useSelector } from "react-redux";
import { translate } from "../../utils/utils";
import { TextField } from "@mui/material";
import { Search } from "tabler-icons-react";
import { InputAdornment } from "@mui/material";
import { fetchQuestionById } from "../../redux/actions/questionsActionCreators";
import { fetchAllQuestions } from "../../redux/actions/questionsActionCreators";
import { useMediaQuery } from "@mui/material";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "#F3FBF9",
    color: "#B1B4BC",
    height: "50px",
    padding: "0px 16px 0px 16px",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    borderBottom: "1px solid rgba(0, 0, 0, 0.1)",
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {},
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

export default function BrowseQuestions({
  open,
  handleClose,
  setFiledValue,
  handleQuestionSelected,
}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const lang = localStorage.getItem("i18nextLng");
  const isMobile = useMediaQuery("(max-width:800px)");
  const storedUser = localStorage.getItem("user");
  const enterpriseAdmin = JSON.parse(storedUser).role === "enterprise_admin" || JSON.parse(storedUser)?.role === "cytailor_admin";

  const questions = useSelector((state) => state.questions.all);
  const totalPages = useSelector((state) => state.questions.totalPages);

  const selectedQuestion = useSelector((state) => state.questions.selected);

  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const handleQuestion = (el) => {
    dispatch(fetchQuestionById(el?.id));
  };

  useEffect(() => {
    if (enterpriseAdmin) {
      dispatch(fetchAllQuestions(lang, page, search));
    }
  }, [dispatch, lang, page, search]);

  useEffect(() => {
    if (selectedQuestion && open) {
      if (lang === "en") {
        setFiledValue("title", selectedQuestion?.[translate("title")]);
        setFiledValue(
          "choices",
          selectedQuestion?.choices?.map((el) => ({
            title: el?.[translate("title")],
            weight: "",
          }))
        );
      } else {
        setFiledValue("title_fr", selectedQuestion?.title_fr);
        setFiledValue(
          "choices",
          selectedQuestion?.choices?.map((el) => ({
            title_fr: el?.title_fr,
            weight: "",
          }))
        );
      }
      handleQuestionSelected();
      handleClose();
    }
  }, [selectedQuestion]);

  const handleScroll = (event) => {
    const { scrollTop, clientHeight, scrollHeight } = event.currentTarget;
    if (scrollHeight - scrollTop === clientHeight && totalPages > page) {
      setPage(page + 1);
    }
  };

  const handleSearchText = (value) => {
    setSearch(value);
    setPage(1);
  };

  return (
    <div>
      <Dialog
        open={open}
        onClose={() => {
          handleClose();
          setSearch("");
        }}
        maxWidth="xl"
        PaperProps={{
          sx: {
            width: isMobile ? "100%" : "65%",
            padding: isMobile ? "5px" : "20px",
          },
        }}
      >
        <Typography
          variant="subtitle3"
          sx={{ display: "flex", alignItems: "center", mb: 1 }}
        >
          <IconButton
            onClick={() => {
              handleClose();
              setSearch("");
            }}
          >
            <KeyboardBackspaceIcon sx={{ width: "70%", color: "black" }} />
          </IconButton>
          {t("back")}
        </Typography>
        <DialogTitle>
          <Typography variant="title4">Browse questions</Typography>
        </DialogTitle>
        <div style={{ padding: "0 24px" }}>
          <TextField
            fullWidth
            id="outlined-start-adornment"
            placeholder={t("search")}
            onChange={(e) => handleSearchText(e.target.value)}
            value={search}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  {" "}
                  <Search size={22} strokeWidth={2} color="#8F9BB2" />
                </InputAdornment>
              ),
            }}
          />
        </div>
        <DialogContent>
          <TableContainer
            component={Paper}
            onScroll={handleScroll}
            className="scrollhost"
            sx={{
              width: "100%",
              height: "530px",
              overflowY: "scroll",
              "&.MuiTableContainer-root": {
                borderRadius: "0px",
                boxShadow: "none",
              },
              mt: 3,
            }}
          >
            <Table
              stickyHeader
              aria-label="customized table"
              sx={{
                width: "100%",
                "& .MuiTableRow-root:hover": {
                  backgroundColor: "#DDF6F0",
                  cursor: "pointer",
                },
              }}
            >
              <TableHead>
                <TableRow>
                  <StyledTableCell
                    align="left"
                    width={isMobile ? "70%" : "100%"}
                  >
                    Questions
                  </StyledTableCell>
                  {!isMobile && (
                    <StyledTableCell align="left" width="30%">
                      {t("related_usecase")}
                    </StyledTableCell>
                  )}
                </TableRow>
              </TableHead>

              <TableBody>
                {questions?.map((el) => (
                  <StyledTableRow
                    key={el.id}
                    onClick={() => {
                      handleQuestion(el);
                    }}
                  >
                    <StyledTableCell align="left">
                      <span>
                        {el?.[translate("title")] || t("not_specified")}
                      </span>
                    </StyledTableCell>
                    {!isMobile && (
                      <StyledTableCell align="left">
                        <span>{el?.uc_title || t("not_specified")}</span>
                      </StyledTableCell>
                    )}
                  </StyledTableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
      </Dialog>
    </div>
  );
}
